import React, { useState } from 'react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import CreateCategoryModal from './CreateCategoryModal';

// Sortable Item Component
function SortableCategoryItem({ category, activeView, onSelectView, styles, onEdit, onDelete, children }) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ id: category.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        position: 'relative'
    };

    const [showActions, setShowActions] = useState(false);

    const handleActionClick = (e, action) => {
        e.stopPropagation(); // Prevent navigation
        action();
    };

    return (
        <div ref={setNodeRef} style={style}>
            <div
                style={{
                    ...styles.treeItem,
                    ...(activeView === `category-${category.id}` ? styles.activeTreeItem : {}),
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    paddingRight: '10px'
                }}
                onClick={() => onSelectView(`category-${category.id}`)}
                onMouseEnter={() => setShowActions(true)}
                onMouseLeave={() => setShowActions(false)}
            >
                <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                    <span
                        {...attributes}
                        {...listeners}
                        style={{ cursor: 'grab', marginRight: '8px', opacity: 0.5 }}
                        title="Drag to reorder"
                    >
                        ☰
                    </span>
                    {category.name}
                </div>

                {showActions && (
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                            style={styles.actionBtn}
                            onClick={(e) => handleActionClick(e, () => onEdit(category))}
                            title="Edit"
                        >
                            ✏️
                        </button>
                        <button
                            style={{ ...styles.actionBtn, color: '#ff4d4f' }}
                            onClick={(e) => handleActionClick(e, () => onDelete(category))}
                            title="Delete"
                        >
                            ❌
                        </button>
                    </div>
                )}
            </div>
            {children}
        </div>
    );
}

export default function Sidebar({
    categories, uncategorizedItems, activeView, onSelectView,
    onCreateCategory, onUpdateCategory, onDeleteCategory, onReorderCategories,
    isDarkMode, onLogout, restaurant
}) {
    const [isMenuOpen, setIsMenuOpen] = useState(true);
    const [editCategory, setEditCategory] = useState(null); // Category to edit

    const styles = getStyles(isDarkMode);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (active.id !== over.id) {
            const oldIndex = categories.findIndex(c => c.id === active.id);
            const newIndex = categories.findIndex(c => c.id === over.id);

            const newOrder = arrayMove(categories, oldIndex, newIndex);
            // Optimistic update should happen in parent but for sidebar list visual we need parent to update 'categories' prop
            onReorderCategories(newOrder);
        }
    };

    const handleEditClick = (cat) => {
        setEditCategory(cat);
    };

    const handleDeleteClick = (cat) => {
        if (window.confirm(`Are you sure you want to delete "${cat.name}"? Items will be uncategorized.`)) {
            onDeleteCategory(cat.id);
        }
    };

    // Filter root items for sorting (nested sorting is complex, we'll sort roots for now)
    const rootCategories = categories.filter(c => !c.parentId);

    return (
        <div style={styles.sidebar}>
            <div style={styles.logo}>
                {restaurant && <h5 style={{ margin: '0 0 5px 0', color: '#0F9D58' }}>{restaurant.name}</h5>}
                <h2>Admin Portal</h2>
            </div>

            <div style={styles.nav}>
                <div
                    style={{ ...styles.navItem, ...(activeView === 'overview' ? styles.active : {}) }}
                    onClick={() => onSelectView('overview')}
                >
                    Dashboard Overview
                </div>

                <div
                    style={{ ...styles.navItem, ...(activeView === 'orders' ? styles.active : {}) }}
                    onClick={() => onSelectView('orders')}
                >
                    Orders <span style={{ fontSize: '0.7em', padding: '2px 5px', borderRadius: '4px', backgroundColor: '#e74c3c', color: 'white', marginLeft: '5px' }}>Live</span>
                </div>

                <div
                    style={{ ...styles.navItem, ...(activeView === 'history' ? styles.active : {}) }}
                    onClick={() => onSelectView('history')}
                >
                    Order History
                </div>

                <div
                    style={{ ...styles.navItem, ...(activeView === 'offers' ? styles.active : {}) }}
                    onClick={() => onSelectView('offers')}
                >
                    Offers & Coupons
                </div>

                <div style={styles.sectionHeader} onClick={() => setIsMenuOpen(!isMenuOpen)}>
                    <span>Menu Management</span>
                    <span>{isMenuOpen ? '▼' : '▶'}</span>
                </div>

                {isMenuOpen && (
                    <div style={styles.tree}>
                        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                            <SortableContext items={rootCategories.map(c => c.id)} strategy={verticalListSortingStrategy}>
                                {rootCategories.map(cat => (
                                    <SortableCategoryItem
                                        key={cat.id}
                                        category={cat}
                                        activeView={activeView}
                                        onSelectView={onSelectView}
                                        onEdit={handleEditClick}
                                        onDelete={handleDeleteClick}
                                        styles={styles}
                                    >
                                        {/* Render Children (Not sortable for now to keep it simple) */}
                                        {categories
                                            .filter(child => child.parentId === cat.id)
                                            .map(child => (
                                                <div
                                                    key={child.id}
                                                    style={{
                                                        ...styles.treeItem,
                                                        paddingLeft: '45px',
                                                        fontSize: '0.9rem',
                                                        position: 'relative',
                                                        ...(activeView === `category-${child.id}` ? styles.activeTreeItem : {})
                                                    }}
                                                    onClick={() => onSelectView(`category-${child.id}`)}
                                                >
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <span>└ {child.name}</span>
                                                        <div className="child-actions" style={{ display: 'flex', gap: '5px' }}>
                                                            <button
                                                                style={styles.actionBtnSmall}
                                                                onClick={(e) => { e.stopPropagation(); setEditCategory(child); }}
                                                            >✏️</button>
                                                            <button
                                                                style={{ ...styles.actionBtnSmall, color: 'red' }}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    if (window.confirm(`Delete ${child.name}?`)) onDeleteCategory(child.id);
                                                                }}
                                                            >❌</button>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))
                                        }
                                    </SortableCategoryItem>
                                ))}
                            </SortableContext>
                        </DndContext>
                        <div style={styles.addCategory} onClick={onCreateCategory}>
                            + New Category
                        </div>
                    </div>
                )}

                {uncategorizedItems && uncategorizedItems.length > 0 && (
                    <div
                        style={{ ...styles.navItem, color: '#e67e22', ...(activeView === 'uncategorized' ? styles.active : {}) }}
                        onClick={() => onSelectView('uncategorized')}
                    >
                        Uncategorized Items
                        <span style={{ fontSize: '0.8em', marginLeft: '8px', opacity: 0.8 }}>({uncategorizedItems.length})</span>
                    </div>
                )}

                <div
                    style={{ ...styles.navItem, ...(activeView === 'settings' ? styles.active : {}) }}
                    onClick={() => onSelectView('settings')}
                >
                    Settings
                </div>
            </div>

            <div style={styles.footer}>
                <button onClick={onLogout} style={styles.logoutBtn}>Logout</button>
            </div>

            {/* Edit Modal (Reusing Create Modal) */}
            {editCategory && (
                <CreateCategoryModal
                    isOpen={!!editCategory}
                    onClose={() => setEditCategory(null)}
                    onCreate={() => { }} // Not used
                    onUpdate={onUpdateCategory}
                    initialData={editCategory}
                    categories={categories}
                    isDarkMode={isDarkMode}
                />
            )}
        </div>
    );
}

const getStyles = (isDark) => ({
    sidebar: {
        width: '250px',
        backgroundColor: isDark ? '#1e1e1e' : '#f5f5f5',
        color: isDark ? '#fff' : '#333',
        height: '100vh',
        position: 'fixed',
        left: 0,
        top: 0,
        display: 'flex',
        flexDirection: 'column',
        borderRight: isDark ? '1px solid #333' : '1px solid #ddd',
        transition: 'background-color 0.3s, color 0.3s',
        zIndex: 100 // Ensure above other checks
    },
    logo: {
        padding: '20px',
        borderBottom: isDark ? '1px solid #333' : '1px solid #ddd'
    },
    nav: {
        flex: 1,
        padding: '10px 0',
        overflowY: 'auto'
    },
    navItem: {
        padding: '12px 20px',
        cursor: 'pointer',
        fontSize: '1rem',
        ':hover': {
            backgroundColor: isDark ? '#333' : '#e0e0e0'
        }
    },
    active: {
        backgroundColor: isDark ? '#2c3e50' : '#e3f2fd',
        color: isDark ? '#64b5f6' : '#1976d2',
        borderRight: '3px solid #1976d2'
    },
    sectionHeader: {
        padding: '12px 20px',
        cursor: 'pointer',
        fontWeight: 'bold',
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: '10px',
        color: isDark ? '#aaa' : '#666'
    },
    tree: {
        paddingLeft: '10px'
    },
    treeItem: {
        padding: '8px 20px 8px 10px', // Adjusted padding
        cursor: 'pointer',
        fontSize: '0.95rem',
        color: isDark ? '#ccc' : '#555',
        borderLeft: isDark ? '1px solid #333' : '1px solid #ddd',
        marginLeft: '15px',
        marginBottom: '2px',
        borderRadius: '4px',
        userSelect: 'none'
    },
    activeTreeItem: {
        backgroundColor: isDark ? '#333' : '#e0e0e0',
        fontWeight: 'bold',
        color: isDark ? '#fff' : '#000'
    },
    addCategory: {
        padding: '8px 20px 8px 30px',
        cursor: 'pointer',
        fontSize: '0.9rem',
        color: '#0F9D58',
        marginLeft: '15px',
        marginTop: '10px'
    },
    footer: {
        padding: '20px',
        borderTop: isDark ? '1px solid #333' : '1px solid #ddd'
    },
    logoutBtn: {
        width: '100%',
        padding: '10px',
        backgroundColor: '#f44336',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer'
    },
    actionBtn: {
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        fontSize: '1rem',
        padding: '2px'
    },
    actionBtnSmall: {
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        fontSize: '0.8rem',
        padding: '2px'
    }
});
